import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { ProcessMapsScreen } from '@/components/discovery/maps/process-maps-screen';

/**
 * Discovery -> Process maps (T-0189, E12-F09): current-state maps in BPMN
 * with a lane per role, drawn by AI, changed by chat or on the canvas,
 * validated by the client and downloaded as .bpmn. The screen is
 * `components/discovery/maps/process-maps-screen.tsx`; this route owns the
 * selected process so a map can be linked: `?process=<process id>`. T-0208
 * adds the future state: `&future=<vendor product id>` (or `choose`, the
 * product picker), so a future-state map can be linked too.
 */
interface MapsSearch {
  process?: string;
  future?: string;
}

export const Route = createFileRoute('/engagements/$engagementId/discovery/maps')({
  validateSearch: (search: Record<string, unknown>): MapsSearch => ({
    ...(typeof search.process === 'string' && search.process ? { process: search.process } : {}),
    ...(typeof search.future === 'string' && search.future ? { future: search.future } : {}),
  }),
  component: MapsRoute,
});

function MapsRoute() {
  const { engagementId } = Route.useParams();
  const { process, future } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const onProcessChange = useCallback(
    (id: string) => void navigate({ search: { process: id }, replace: true }),
    [navigate],
  );
  const onFutureChange = useCallback(
    (next: string | null) =>
      void navigate({
        search: (prev) => ({
          ...(prev.process ? { process: prev.process } : {}),
          ...(next ? { future: next } : {}),
        }),
        replace: true,
      }),
    [navigate],
  );
  return (
    <ProcessMapsScreen
      engagementId={engagementId}
      processId={process ?? null}
      onProcessChange={onProcessChange}
      future={future ?? null}
      onFutureChange={onFutureChange}
    />
  );
}
